import { computed, unref, type MaybeRefOrGetter } from 'vue';

import { useCurrentCalculation } from '@/composables/useCurrentCalculation';
import { useCurrentCalculatorSegments } from '@/composables/useCurrentCalculatorSegments';
import { useCalculationEngine } from '@/composables/useCalculationEngine';

import { fixNumbers } from '@/utils/math';

export interface SegmentRuleFilters {
  /**
   * Whether to include only visible rules, only invisible rules, or all rules
   * @default 'visible-only'
   */
  visibility?: 'visible-only' | 'invisible-only' | 'all';

  /**
   * Whether to include only hidden rules, only non-hidden rules, or all rules
   * @default 'unhidden-only'
   */
  hidden?: 'unhidden-only' | 'all';

  /**
   * Whether to include rules with parent_id !== null
   * @default false
   */
  includeChildRules?: boolean;

  /**
   * Rule types to include. If not specified, includes all types
   */
  ruleTypes?: string[];

  /**
   * Whether to include segment-level rules
   * @default true
   */
  includeSegmentRules?: boolean;

  /**
   * Whether to include group-level rules
   * @default true
   */
  includeGroupRules?: boolean;
}

export function useEstimatedSegments(filters: MaybeRefOrGetter<SegmentRuleFilters> = {}) {
  const { currentCalculation } = useCurrentCalculation();
  const { getSegmentById } = useCurrentCalculatorSegments();
  const { evalRulesWithExpression } = useCalculationEngine();

  const segmentRules = computed(() => {
    if (!currentCalculation.value) {
      return null;
    }

    const currentFilters = unref(filters) as SegmentRuleFilters;
    const visibility = currentFilters.visibility ?? 'visible-only';
    const hidden = currentFilters.hidden ?? 'unhidden-only';
    const includeChildRules = currentFilters.includeChildRules ?? false;
    const ruleTypes = currentFilters.ruleTypes ?? ['calculation'];
    const includeSegmentRules = currentFilters.includeSegmentRules ?? true;
    const includeGroupRules = currentFilters.includeGroupRules ?? true;

    const createRuleFilter = () => (rule: any) => {
      if (!includeChildRules && rule.parent_id !== null) {
        return false;
      }

      if (visibility === 'visible-only' && !rule.is_visible) {
        return false;
      }
      if (visibility === 'invisible-only' && rule.is_visible) {
        return false;
      }

      if (hidden === 'unhidden-only' && rule.is_hidden) {
        return false;
      }

      return !(ruleTypes.length > 0 && !ruleTypes.includes(rule.type));
    };

    const ruleFilter = createRuleFilter();

    return currentCalculation.value.calculation_segments.map((segment) => {
      const segmentData = getSegmentById(segment.segment_id);

      if (!segmentData) {
        throw Error(`Nie znaleziono segmentu o id ${segment.segment_id}`);
      }

      return {
        ...segmentData,
        groups: includeGroupRules
          ? segmentData.view_groups.map((group) => ({
            ...group,
            rules: group.rules.filter(ruleFilter).map((rul) => {
              const evalWithExpression = evalRulesWithExpression(rul.rules);
              return {
                ...rul,
                eval:
                  rul.unit === "%"
                    ? (Number(evalWithExpression.eval) * 100).toFixed(2)
                    : fixNumbers(evalWithExpression.eval),
                expression: evalWithExpression.expression,
              };
            }),
          }))
          : [],
        rules: includeSegmentRules
          ? segmentData.rules.filter(ruleFilter).map((rul) => {
            const evalWithExpression = evalRulesWithExpression(rul.rules);
            return {
              ...rul,
              eval:
                rul.unit === "%"
                  ? (Number(evalWithExpression.eval) * 100).toFixed(2)
                  : fixNumbers(evalWithExpression.eval),
              expression: evalWithExpression.expression,
            };
          })
          : [],
      };
    });
  });

  const segmentGroupRules = computed(() => {
    return segmentRules.value?.map((segment) => {
      return {
        ...segment,
        groups: segment.groups.filter((group) => group.rules.length),
      };
    });
  });

  const financingSegments = computed(() => {
    return segmentGroupRules.value?.filter((segment) => segment.name === 'Finansowanie Raty') || [];
  });

  const regularSegments = computed(() => {
    return segmentGroupRules.value?.filter((segment) => segment.name !== 'Finansowanie Raty') || [];
  });

  const hasFinancingSegments = computed(() => {
    return financingSegments.value.length > 0;
  });

  return {
    segmentRules,
    segmentGroupRules,
    financingSegments,
    regularSegments,
    hasFinancingSegments,
  };
}
